import { useTranslation } from 'react-i18next';
import { formatNumber } from '../lib/format';
import type { Language } from '../i18n';

export const PAGE_SIZES = [12, 25, 50, 100] as const;

/** Páginas a mostrar: primera, última y las vecinas de la actual; 0 representa un salto ("…"). */
export function pageWindow(page: number, pages: number): number[] {
  const out: number[] = [];
  for (let p = 1; p <= pages; p += 1) {
    if (p === 1 || p === pages || Math.abs(p - page) <= 1) out.push(p);
    else if (out[out.length - 1] !== 0) out.push(0);
  }
  return out;
}

/** Paginador de tablas: rango visible, total, tamaño de página y navegación. */
export function Pager({
  id, page, pageSize, total, onPage, onPageSize,
}: {
  id: string;
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="pager" id={id}>
      <span className="help" data-role="range">
        {t('pager.showing', { from: formatNumber(from, lang), to: formatNumber(to, lang), total: formatNumber(total, lang) })}
      </span>
      <div className="pagernav">
        <select className="select" aria-label={t('pager.perPageLabel')} data-role="size" value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {t('pager.perPage', { n })}
            </option>
          ))}
        </select>
        <button type="button" className="btn sm" data-role="prev" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          {t('pager.prev')}
        </button>
        {pageWindow(page, pages).map((p, i) =>
          p === 0 ? (
            <span className="muted" key={`gap-${i}`}>
              {t('pager.gap')}
            </span>
          ) : (
            <button type="button" key={p} className={p === page ? 'btn sm black' : 'btn sm'} data-page={p} aria-current={p === page ? 'page' : undefined} onClick={() => onPage(p)}>
              {formatNumber(p, lang)}
            </button>
          ),
        )}
        <button type="button" className="btn sm" data-role="next" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          {t('pager.next')}
        </button>
      </div>
    </div>
  );
}
